// src/auth/pages/NoAccessPage.tsx
import React, { useCallback } from 'react';
import { Button, Spin } from 'antd';
import { LogoutOutlined, LockOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import { selectProfile } from '@store/slices/userSlice.tsx';
import { logout } from '@store/slices/authSlice.tsx';
import { RootState } from '@store/store.tsx';

import './NoAccessPage.css';

/**
 * Página genérica para usuarios autenticados que no poseen acceso
 * a ninguna sección del sistema (e.g. solo ERP habilitado y el
 * usuario no es staff).  Muestra la información básica del perfil
 * y un botón de cerrar sesión para evitar el redirect loop.
 */
export const NoAccessPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector(selectProfile);
    const authState = useAppSelector((s: RootState) => s.auth);
    const { isAuthenticated } = authState;
    const isLoading = authState.status === 'loading' || authState.status === 'checking';
    const siteSettings = useAppSelector((s: RootState) => s.settings.site);
    const accessDenied = new URLSearchParams(location.search).get('reason') === 'google_access_denied';

    const siteName = siteSettings?.name?.trim() || null;
    const logoUrl = siteSettings?.logo?.thumbnails?.default || null;

    const handleLogout = useCallback(async () => {
        try {
            await dispatch(logout()).unwrap();
        } finally {
            navigate('/auth/login', { replace: true });
        }
    }, [dispatch, navigate]);

    // La denegación Google es pública y no debe iniciar un loop hacia login.
    if (!accessDenied && !isAuthenticated && !isLoading) {
        navigate('/auth/login', { replace: true });
        return null;
    }

    if (!accessDenied && isLoading) {
        return (
            <div className="no-access-page">
                <Spin size="large" />
            </div>
        );
    }

    const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.username || '—';

    return (
        <div className="no-access-page">
            {/* Logo / nombre del sitio */}
            {(logoUrl || siteName) && (
                <div className="no-access-brand">
                    {logoUrl && (
                        <img
                            src={logoUrl}
                            alt={siteName || 'Logo'}
                            className="no-access-logo"
                        />
                    )}
                    {siteName && <span className="no-access-site-name">{siteName}</span>}
                </div>
            )}

            {/* Card principal */}
            <div className="no-access-card">
                <div className="no-access-icon-wrapper">
                    <LockOutlined className="no-access-icon" />
                </div>

                <h1 className="no-access-title">Sin acceso</h1>
                <p className="no-access-subtitle">
                    {accessDenied
                        ? 'El inicio de sesión fue validado, pero esta cuenta no tiene acceso habilitado para este sitio.'
                        : 'Tu cuenta no tiene permisos para acceder a ninguna sección del sistema. Contactá al administrador si creés que es un error.'}
                </p>

                {/* Datos del usuario */}
                {!accessDenied && <div className="no-access-info">
                    <div className="no-access-info-row">
                        <span className="no-access-label">Nombre</span>
                        <span className="no-access-value">{fullName}</span>
                    </div>
                    {profile?.email && (
                        <div className="no-access-info-row">
                            <span className="no-access-label">Email</span>
                            <span className="no-access-value">{profile.email}</span>
                        </div>
                    )}
                    {profile?.phone && (
                        <div className="no-access-info-row">
                            <span className="no-access-label">Teléfono</span>
                            <span className="no-access-value">{profile.phone}</span>
                        </div>
                    )}
                </div>}

                {/* Botón de logout */}
                {accessDenied ? <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate('/auth/login', { replace: true })}
                    block
                    className="no-access-logout-btn"
                >
                    Volver al inicio de sesión
                </Button> : <Button
                    type="primary"
                    danger
                    size="large"
                    icon={<LogoutOutlined />}
                    onClick={handleLogout}
                    loading={isLoading}
                    block
                    className="no-access-logout-btn"
                >
                    Cerrar sesión
                </Button>}
            </div>
        </div>
    );
};
